/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import React, { CSSProperties, useMemo } from "react";
import { Modal, Dialog, XIcon } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import { ChatBot } from "@/components/molecules/cta-button/cta-buttons";
import modalStyles from "@/components/molecules/questions-container/cover-type/cover-type-modal/cover-type-modal.module.css";
import DetailSection from "@/components/molecules/questions-container/cover-type/cover-type-modal/detail-section/detail-section";
import withBrand from "@/components/with-brand";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { DontWantRenewModalType } from "@/schema/renewal-page/page-content.schema";
import { BrandKey } from "@/types/common-types";
import { getDivStyle } from "@/utils/cover-type.utils";
import { showMessenger } from "@/utils/cxone.utils";

type DontWantToRenewModalPropsType = {
  brand?: BrandKey;
  modalContent: DontWantRenewModalType;
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
};

function DontWantToRenewModal({
  brand = "staysure",
  modalContent,
  isOpen,
  setIsOpen,
}: Readonly<DontWantToRenewModalPropsType>) {
  const {
    title,
    subtitle,
    content,
    dontWantToRenewModalCtaLabel,
    dontWantToRenewModalSecondaryCtaLabel,
    backgroundModalImageUrl,
    modalInfoContent,
  } = modalContent;
  const extractDataFromHTML = (
    htmlString: string
  ): { header: string; description: string }[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const extractedData: { header: string; description: string }[] = [];
    const listItems = doc.querySelectorAll("ul > li"); // Select only top-level <li> items

    listItems.forEach((li) => {
      // Get header text from the first text node of <li> (ignores nested <ul>)

      const header = li.querySelector("strong")?.textContent?.trim() || "";

      // Get the first <ul> -> <li> for the description
      const description =
        li.querySelector("ul > li")?.textContent?.trim() || "";
      // const header = titleNode ? titleNode.textContent?.trim() || "" : "";

      // Get description from the first <ul> -> <li>
      // const description = li.querySelector("ul > li")?.textContent.trim() || "";

      if (header && description) {
        extractedData.push({ header, description });
      }
    });

    return extractedData;
  };

  const richTextContent = extractDataFromHTML(content);
  const styles = useMemo(() => getDivStyle(brand), [brand]);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const UnderlayStyles: CSSProperties = {
    zIndex: 9999,
    background: "rgba(26, 26, 26, 0.6)",
  };

  const ModalStyles: CSSProperties = {
    display: "flex",
    background: "white",
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    borderRadius: 8,
  };
  const isdesktop = useMediaQuery("(min-width: 600px)");

  const handleCall = (number: string) => {
    const numberToCall = /\d+/.exec(number.split(" ").join(""));
    return `tel:${numberToCall?.[0]}`;
  };

  const handleCallClick = () => {
    const telUri = handleCall(dontWantToRenewModalCtaLabel);
    window.location.href = telUri;
  };

  const handleChatNowClick = () => {
    showMessenger();
    setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  return (
    <Modal
      className="single-type-modal md:w-[660px] w-[327px]"
      isOpen={isOpen}
      underlayStyles={UnderlayStyles as any}
      modalStyles={ModalStyles as any}
      isDismissable
    >
      <Dialog className="w-full overflow-auto max-h-screen">
        <div
          data-testid="cover_type_modal"
          className=" p-spacing-l bg-surface-neutral-option-1 rounded-lg justify-start md:gap-spacing-xl flex md:flex-row  flex-col-reverse w-[327px] md:w-full"
        >
          <div
            className="md:w-[375px] w-full flex-col justify-start items-start gap-spacing-xs
    inline-flex relative md:px-0 md:pt-0 md:pb-0"
          >
            <div
              className={`self-stretch text-text-title font-header-font md:text-[31px] text-[23px] md:leading-[34px] leading-[28px] md:left-0 md:top-0
        left-spacing-m md:w-full z-0`}
            >
              {title}
            </div>
            <div className="flex-col flex justify-start items-start gap-spacing-m">
              {subtitle && (
                <div
                  className="self-stretch leading-[24px] text-2xs font-normal font-body-font
            md:left-0 md:top-0 left-spacing-m  md:w-full  md:z-0 z-[1] text-text-body"
                >
                  {subtitle}
                </div>
              )}
              <div className="self-stretch flex-col justify-start items-start gap-spacing-s flex ">
                {richTextContent.map((item) => (
                  <DetailSection
                    key={item.header}
                    title={item.header}
                    description={item.description}
                    color={styles.check_cricle_color}
                  />
                ))}
              </div>
              <div
                className={`self-stretch h-12 flex-col justify-center md:items-start items-center
     flex md:w-[243px] w-full ${modalStyles.btnWrapper}`}
              >
                <LabelButtonWrapper
                  id="cover-type-modal-primary-button"
                  data-testid="cover-type-modal-primary-button"
                  className=" !text-confirmation-body"
                  label={dontWantToRenewModalCtaLabel ?? ""}
                  hierarchy="primary"
                  type="button"
                  onClick={() => {
                    handleCallClick();
                    setTimeout(() => {
                      setIsOpen(false);
                    }, 100);
                  }}
                />
              </div>
              <div
                className={`self-stretch h-12 flex-col justify-center md:items-start items-center
     flex md:w-[243px] w-full ${modalStyles.btnWrapper}`}
              >
                <ChatBot
                  id="cancel-call-modal-secondary-button"
                  data-testid="cancel-call-modal-secondary-button"
                  hierarchy="secondary"
                  label={dontWantToRenewModalSecondaryCtaLabel ?? ""}
                  onClick={handleChatNowClick}
                  className="text-interactive-secondary-default !h-[48px] w-full font-semibold leading-[18px]"
                />
              </div>
            </div>
          </div>
          <div className={`${styles.image_div_width_height} w-full relative `}>
            <button
              id="single-type-modal-close-button"
              data-testid="single-type-modal-close-button"
              type="button"
              className=" absolute md:right-[-1px] md:top-[-1px] md:bg-surface-neutral-option-1
        bg-transparent md:w-[45px] md:h-[35px] flex items-start top-0 right-0 justify-end rounded-bl-radius-lg z-[3]"
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(false);
              }}
              aria-label="close modal"
            >
              <XIcon stroke="#306582 " />
              <div
                className={` ${modalStyles.btnTopLeftCurve} md:block hidden`}
              />
              <div
                className={` ${modalStyles.btnBotmRightCurve} md:block hidden`}
              />
            </button>
            {isdesktop && (
              <>
                <img
                  src={`${process.env.NEXT_PUBLIC_MGNL_HOST}/dam/${backgroundModalImageUrl?.["@id"]}${backgroundModalImageUrl?.["@path"]}`}
                  className={` absolute top-0 left-0 w-full h-full rounded-t-radius-lg md:rounded-b-radius-lg object-cover z-[1] ${
                    !imageLoaded ? "hidden" : ""
                  } ${styles.image_range}`}
                  data-testid="banner-image-testid"
                  onLoad={() => {
                    setImageLoaded(true);
                  }}
                  alt="background-dont want to renew"
                />
                {!imageLoaded && (
                  <div className="w-full h-full rounded-t-radius-lg md:rounded-b-radius-lg animate-loading z-[2]" />
                )}
              </>
            )}
          </div>
        </div>
        <div className="pt-spacing-xs pb-spacing-l px-spacing-l bg-surface-neutral-option-1 rounded-lg justify-start items-center md:gap-spacing-xl flex md:flex-row  flex-col-reverse w-[327px] md:w-full">
          <div className="text-text-body text-2xs text-left leading-custom-size-lg ">
            <RichText
              richText={modalInfoContent}
              className=" [&_strong]:font-[600]
  flex flex-col gap-spacing-m [&>ul]:list-disc [&>ul>li]:marker:text-4xs [&>ul]:ml-[27px] [&>ul>li]:pb-spacing-xs [&>ul>li:last-child]:pb-0  [&>p:last-child]:text-3xs [&>p:last-child]:leading-[21px]"
            />
          </div>
        </div>
      </Dialog>
    </Modal>
  );
}

export default withBrand(DontWantToRenewModal);
